import  React, { useEffect, useRef } from 'react';
import dayjs from 'dayjs'; 
import { Alert, Button, Card, Layout, Space, Table } from 'antd';
import { EditOutlined, PlusOutlined} from '@ant-design/icons'; 
import { Routes } from '../../../routes'; 
import useTags from '../../gql/hooks/useTags'; 
import useDeleteTag from '../../gql/hooks/useDeleteTag'; 
import Spinner from '../../components/Spinner/Spinner';
import useHasError from '../../hooks/useHasError'; 
import useIsLoading from '../../hooks/useIsLoading'; 
import useSearchFilter from '../../hooks/useSearchFilter'; 
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import SearchBar from '../../components/SearchBar/SearchBar';
import { useURLQueries } from '../../hooks/useURLQueries'; 
import { renderWithSearchWords } from '../../components/SearchBar/SearchBar';
import TagCreate from '../../components/TagCreate/TagCreate';
import TagEdit from '../../components/TagEdit/TagEdit';
import './styles.less';

// Applied to every column except the greedy name column: keeps headers and
// cell contents on a single line so they aren't compressed into wrapping.
const noWrap = {
    onHeaderCell: () => ( { style: { whiteSpace: 'nowrap' } } ),
    onCell: () => ( { style: { whiteSpace: 'nowrap' } } ),
};

const TagsListPage = ( props ) => {
    const editTagDrawerRef = useRef( null );
    const createTagModalRef = useRef( null );

    const { fetchTags, tagsLoading, tagsError, tags } = useTags();
    const { currentPage, setCurrentPage, searchText, setSearchText, searchWords, 
        sortField, setSortField, sortDirection, setSortDirection } = useURLQueries();
    const { filter, filteredData } = useSearchFilter();
    const { isLoading } = useIsLoading( { items: [ tagsLoading ] } );
    const { hasError } = useHasError( { items: [ tagsError ] } );
    const { deleteTag } = useDeleteTag();

    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Content Taxonomy', path: Routes.ContentTaxonomyNav.buildPath() },
        { name: 'Tags' }
    ];
 
    useEffect( () => {
        if ( !isLoading && !hasError && tags && searchText ) {
            filter( searchText, tags );
        }
    }, [ isLoading, hasError, tags ] );

    const createdTagCallback = () => {
        fetchTags();
    };

    const editedTagCallback = () => {
        fetchTags();
    };

    const handleDeleteClick = async ( id ) => {
        await deleteTag( id );
        fetchTags();
    };

    const handleCreateClick = () => {
        createTagModalRef.current.showModal();
    };
 
    const handleEditClick = ( item ) => {
        editTagDrawerRef.current.showDrawer( item );    
    };

    const handleSearchClick = ( value ) => {
        setCurrentPage( 1 );
        setSortField( 'name' );
        setSortDirection( 'ascend' );
        setSearchText( value );
        filter( value, tags );
    };

    const handleTableChange = ( pagination, filters, sorter ) => {
        setSortField( sorter?.columnKey );
        setSortDirection( sorter?.order );
    };

    const handlePaginatorChange = ( pagination ) => {
        setCurrentPage( pagination );
    };

    const columns =  [ 
        {
            key: 'name',
            dataIndex: 'name',
            title: 'Name',
            width: '100%',
            sortOrder: sortField === 'name' && sortDirection,   
            sorter: ( a, b ) => a.name.localeCompare( b.name ),
            render: ( name ) => searchText ? renderWithSearchWords( name, searchWords ) : name
        },
        {
            key: 'createdAt',
            dataIndex: 'createdAt',
            title: 'Created At',
            sortOrder: sortField === 'createdAt' && sortDirection,   
            sorter: ( a, b ) => dayjs( a.createdAtFull ).isBefore( dayjs( b.createdAtFull ) ) ? -1 : 1 
        },
        {
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            title: 'Updated At',
            sortOrder: sortField === 'updatedAt' && sortDirection,   
            sorter: ( a, b ) => dayjs( a.updatedAtFull ).isBefore( dayjs( b.updatedAtFull ) ) ? -1 : 1 
        },
         
        {
            key: 'actions', 
            title: 'Actions',
            fixed: 'right', 
            width: '100px',
            render: ( item ) => 
                <>
                    <Space>
                        <Button 
                            type="link" 
                            icon={ <EditOutlined /> } 
                            onClick= { () => handleEditClick( item ) }
                        />
                                
                        <DeleteButton
                            id={ item.id }   
                            title={ 'Confirm Delete' }
                            deleteCallback={ ( i ) => handleDeleteClick( i ) }
                            confirmMessage= { 
                                <>
                                    <p>Are you <i>sure</i> you want to permanently delete the tag <b>{ item.name }</b>? 
                                    This action cannot be undone.</p>
                                </>
                            }
                        />
                    </Space>
                </> 
        } 
    ];

    /*************************************
     *  ANCHOR Render
     *************************************/
   
    return ( 
        <Layout className="tags-list-page">
            
            { /* Page header breadcrumbs */ }
            <Breadcrumbs crumbs={ breadcrumbs }/>

            { /* Loading Spinner */ }
            { isLoading &&
                <Spinner/>
            }
             
            { /* Main Content */ }
            { !isLoading &&
                <Card
                    className="tags-content"
                    title="Tags"
                    extra={ !hasError &&
                        <Button
                            icon={ <PlusOutlined /> }
                            onClick={ () => handleCreateClick() }
                            type="text"
                        >
                            Create New Tag
                        </Button>
                    }
                >   
                    { /* Error message */ }
                    { hasError && 
                        <Alert 
                            type="error"
                            message="Error"
                            description="An unknown error occurred. Please try again later."
                        />
                    }

                    { !hasError &&
                        <>
                            { /* Search bar */ }
                            <SearchBar
                                searchText={ searchText }
                                placeHolerText="Search by name"
                                handleSearchCallback={ handleSearchClick }
                                closeTagCallback={ () => handleSearchClick( '' ) }
                            /> 

                            { /* Data table */ }
                            <Table
                                tableLayout="auto"
                                rowKey='id'
                                columns={ columns.map( c => c.key === 'name' ? c : { ...noWrap, ...c } ) }
                                dataSource={ filteredData || tags }
                                onChange={ handleTableChange }
                                pagination={ {
                                    onChange: handlePaginatorChange,
                                    defaultCurrent: currentPage || 1,
                                    showQuickJumper: false,
                                    showSizeChanger: false
                                } }
                            />

                            { /* Modal for creating a tag */ }
                            <TagCreate 
                                ref={ createTagModalRef } 
                                createdTagCallback={ createdTagCallback }
                            />

                            { /* Drawer for editing a tag */ }
                            <TagEdit 
                                ref={ editTagDrawerRef } 
                                editedTagCallback={ editedTagCallback }
                                deleteTagCallback={ () => fetchTags() }
                            /> 
                        </>
                    }
                </Card>
            }
        </Layout>
    );
};

export default TagsListPage;
